'use client'
import type { CarbonIconType } from '@carbon/icons-react'
import { Alarm, CheckmarkOutline, CloseOutline } from '@carbon/icons-react'
import type { OfferTeaserType } from 'app/_components/OfferTeaser'
import OfferTeaser from 'app/_components/OfferTeaser'
import { useState } from 'react'
import OfferWidgetTabs from './OfferWidgetTabs'

type OfferWidgetListProps = {
  myOffers: OfferTeaserType[]
  approvedOffers: OfferTeaserType[]
  rejectedOffers: OfferTeaserType[]
  newOffers: OfferTeaserType[]
}

export default function OfferWidgetList({
  myOffers,
  approvedOffers,
  rejectedOffers,
  newOffers,
}: OfferWidgetListProps) {
  const [activeOfferTab, setActiveOfferTab] = useState<
    'myOffers' | 'updatedOffers'
  >('myOffers')

  const hasUpdates =
    approvedOffers.length || rejectedOffers.length || newOffers.length

  return (
    <>
      <OfferWidgetTabs
        activeTab={activeOfferTab}
        onTabChange={(tab) => setActiveOfferTab(tab)}
      />
      {activeOfferTab === 'myOffers' &&
        (myOffers.length ? (
          <div className="overflow-y-auto">
            {myOffers.map((offer) => (
              <OfferTeaser key={offer.id} offer={offer} />
            ))}
          </div>
        ) : (
          <span className="text-text-secondary my-4 pl-4 text-sm">
            Keine Offerten
          </span>
        ))}
      {activeOfferTab === 'updatedOffers' && (
        <>
          {hasUpdates ? (
            <>
              <div className="flex gap-4 overflow-y-auto py-4 text-sm xl:flex-col xl:gap-8">
                <OfferWidgetSection
                  title="Zusage erhalten"
                  offers={approvedOffers}
                  Icon={CheckmarkOutline}
                  theme="green"
                />
                <OfferWidgetSection
                  title="Absage erhalten"
                  offers={rejectedOffers}
                  Icon={CloseOutline}
                  theme="red"
                />
                <OfferWidgetSection
                  title="Neue Offerten"
                  offers={newOffers}
                  Icon={Alarm}
                  theme="blue"
                />
              </div>
            </>
          ) : (
            <span className="text-text-secondary my-4 pl-4 text-sm">
              Keine Updates
            </span>
          )}
        </>
      )}
    </>
  )
}

type Theme = 'red' | 'green' | 'blue'

const themeColors: Record<Theme, string> = {
  red: 'text-support-error',
  green: 'text-support-green',
  blue: 'text-support-info',
}

const OfferWidgetSection = ({
  offers,
  Icon,
  title,
  theme = 'red',
}: {
  offers: OfferTeaserType[]
  Icon: CarbonIconType
  title: string
  theme?: Theme
}) => {
  if (!offers.length) return null

  return (
    <section className="flex flex-col gap-2 text-sm">
      <span className={`flex items-center gap-2 ${themeColors[theme]}`}>
        <Icon /> {title}
      </span>
      <div className="flex h-full gap-4 overflow-y-auto xl:flex-col xl:gap-2">
        {offers.map((offer) => (
          <OfferTeaser key={offer.id} offer={offer} includeDate={false} />
        ))}
      </div>
    </section>
  )
}
